// 3 · Créer (ou modifier) un tableau en 3 étapes : catégorie → profils (packs, pêche : espèce,
// postes, leurre et limites personnelles) → lieux et nom. Le brouillon survit à un détour par la
// carte (« + Ajouter un lieu sur la carte »).
import { CATEGORIES, inSeason, paramBounds, profileName, type CatalogTemplateDto, type ParamBounds, type Category, type LocationDto, type TableDto } from '@crenow/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../api.ts';
import { ephemeral } from '../ephemeral.ts';
import { CATEGORY_LABELS, decimal, parisDay, seasonLabel } from '../format.ts';
import { Box, FormError, Loading, MarineBanner, StatusPill, TabBar, useResource } from '../ui.tsx';

interface Draft {
  step: 1 | 2 | 3;
  category: Category;
  /** Champignons, jardin : templates cochés. */
  profiles: number[];
  /** Pêche : espèce, postes et esches cochés (profils = leur produit), limites réglées. */
  fish: number | null;
  postes: number[];
  esches: number[];
  limits: Record<string, number>;
  places: number[];
  name: string;
  nameTouched: boolean;
  /** Modification d'un tableau existant ; profils d'autres catégories ou espèces gardés tels quels. */
  tableId: number | null;
  kept: TableDto['profiles'];
}

const EMPTY: Draft = { step: 1, category: 'champignons', profiles: [], fish: null, postes: [], esches: [], limits: {}, places: [], name: '', nameTouched: false, tableId: null, kept: [] };

/** Brouillon gardé le temps d'un aller-retour vers la carte (vidé à la déconnexion). */
const savedDraft = () => ephemeral.draft as Draft | null;

/** Lieu créé depuis la carte pendant la création : coché au retour. */
export function rememberPlace(id: number) {
  const saved = savedDraft();
  if (saved && !saved.places.includes(id)) ephemeral.draft = { ...saved, places: [...saved.places, id] };
}


const PACK_LABELS: Record<Category, string> = { champignons: 'Tous champignons', peche: 'Toutes espèces', jardin: 'Tout le jardin' };

/**
 * Limites du panneau : une valeur par limite pour tous les postes et esches cochés qui l'acceptent ;
 * sans réglage, la plus prudente de leurs valeurs par défaut (la plus basse pour un maximum).
 */
function panelLimits(chosen: CatalogTemplateDto[], limits: Record<string, number>): { name: string; value: number }[] {
  const defaults = new Map<string, number[]>();
  for (const part of chosen) for (const param of part.params) defaults.set(param.name, [...(defaults.get(param.name) ?? []), param.default]);
  return [...defaults].map(([name, values]) => ({ name, value: limits[name] ?? (name.endsWith('_min') ? Math.max(...values) : Math.min(...values)) }));
}

/**
 * Bornes du curseur d'une limite commune à plusieurs postes ou esches : les plus étroites (plancher
 * le plus haut, plafond le plus bas), pour qu'aucun ne sorte de ses propres bornes.
 */
function sliderBounds(chosen: CatalogTemplateDto[], param: { name: string; value: number }): ParamBounds {
  const known = chosen.flatMap((part) => paramBounds(part.slug, param.name) ?? []);
  if (known.length === 0) return { label: param.name, min: 0, max: Math.max(10, param.value * 2), step: 0.1, unit: '' };
  return { ...known[0]!, min: Math.max(...known.map((bounds) => bounds.min)), max: Math.min(...known.map((bounds) => bounds.max)) };
}

const toggle = (list: number[], id: number) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/** Profils de pêche cochés : produit des postes et des esches (aucun coché : espèce seule). */
function combinations(postes: number[], esches: number[]): { posteId: number | null; escheId: number | null }[] {
  return (postes.length > 0 ? postes : [null]).flatMap((posteId) => (esches.length > 0 ? esches : [null]).map((escheId) => ({ posteId, escheId })));
}

const profileKey = (profile: { templateId: number; posteId: number | null; escheId: number | null }) => `${profile.templateId}+${profile.posteId ?? ''}+${profile.escheId ?? ''}`;

/**
 * Limites enregistrées hors des bornes actuelles (resserrées depuis) : retirées, la valeur par
 * défaut s'applique ; sinon le serveur refuserait tout enregistrement du tableau.
 */
function boundedParams(profile: TableDto['profiles'][number]): Record<string, number> | null {
  if (!profile.params) return null;
  const slugs = [profile.posteSlug, profile.escheSlug].filter((slug): slug is string => slug !== null);
  const inBounds = Object.entries(profile.params).filter(([name, value]) =>
    slugs.every((slug) => {
      const bounds = paramBounds(slug, name);
      return !bounds || (value >= bounds.min && value <= bounds.max);
    }),
  );
  return inBounds.length > 0 ? Object.fromEntries(inBounds) : null;
}

function draftFromTable(stored: TableDto): Draft {
  const table = { ...stored, profiles: stored.profiles.map((profile) => ({ ...profile, params: boundedParams(profile) })) };
  const first = table.profiles[0];
  const category = first?.category ?? 'champignons';
  let fish = category === 'peche' ? (first?.templateId ?? null) : null;
  let mine = table.profiles.filter((profile) => profile.category === category && (category !== 'peche' || profile.templateId === fish));
  const postes = [...new Set(mine.flatMap((profile) => (profile.posteId ? [profile.posteId] : [])))];
  const esches = [...new Set(mine.flatMap((profile) => (profile.escheId ? [profile.escheId] : [])))];
  // Composition que les cases ne décrivent pas (ex. « Bar · kayak » + « Bar · leurre · bord ») :
  // gardée telle quelle avec les autres profils, aucune espèce cochée.
  if (fish !== null) {
    const species = fish;
    const product = new Set(combinations(postes, esches).map((part) => profileKey({ templateId: species, ...part })));
    if (product.size !== mine.length || !mine.every((profile) => product.has(profileKey(profile)))) {
      fish = null;
      mine = [];
    }
  }
  const limits: Record<string, number> = {};
  for (const profile of mine) Object.assign(limits, profile.params ?? {});
  return {
    ...EMPTY,
    step: 2,
    category,
    profiles: category === 'peche' ? [] : mine.map((profile) => profile.templateId),
    fish,
    postes: fish === null ? [] : postes,
    esches: fish === null ? [] : esches,
    limits,
    places: table.locations.map((location) => location.id),
    name: table.name,
    nameTouched: true,
    tableId: table.id,
    kept: table.profiles.filter((profile) => !mine.includes(profile)),
  };
}

export function CreatePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = Number(params.get('tableau')) || null;
  const catalog = useResource<{ templates: CatalogTemplateDto[] }>('/templates');
  const locations = useResource<{ locations: LocationDto[] }>('/locations');
  const [draft, setDraft] = useState<Draft | null>(() => {
    const saved = savedDraft();
    return saved && saved.tableId === editId ? saved : editId ? null : EMPTY;
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const today = parisDay();

  useEffect(() => {
    if (draft || !editId) return;
    api<{ table: TableDto }>('GET', `/tables/${editId}`)
      .then(({ table }) => setDraft(draftFromTable(table)))
      .catch((caught) => setError(errorMessage(caught)));
  }, [draft, editId]);

  useEffect(() => {
    ephemeral.draft = draft;
  }, [draft]);

  const templates = useMemo(() => catalog.data?.templates ?? [], [catalog.data]);
  if (!draft) {
    return (
      <div className="screen">
        <main className="screen-body">{error ? <FormError error={error} /> : <Loading />}</main>
        <TabBar />
      </div>
    );
  }

  const update = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const ofCategory = templates.filter((template) => template.category === draft.category);
  const species = ofCategory.filter((template) => template.role !== 'poste' && template.role !== 'esche');
  const postes = ofCategory.filter((template) => template.role === 'poste');
  const esches = ofCategory.filter((template) => template.role === 'esche');
  const offSeason = (template: CatalogTemplateDto) => !inSeason(today, template.season);
  const names = (ids: number[]) => ids.flatMap((id) => templates.filter((template) => template.id === id).map((template) => template.name));
  const nameOf = (id: number | null) => templates.find((template) => template.id === id)?.name ?? null;
  // Profils conservés, nommés comme partout ailleurs (« Bar · leurre · bord »).
  const keptNames = draft.kept.map((profile) => profileName(profile.name, nameOf(profile.escheId), nameOf(profile.posteId))).join(', ');
  const fish = templates.find((template) => template.id === draft.fish);
  const chosenPostes = postes.filter((poste) => draft.postes.includes(poste.id));
  const chosenEsches = esches.filter((esche) => draft.esches.includes(esche.id));
  const chosenParts = [...chosenPostes, ...chosenEsches];
  const lower = (parts: CatalogTemplateDto[]) => (parts.length > 0 ? [parts.map((part) => part.name.toLocaleLowerCase('fr-FR')).join(', ')] : []);

  // « Pêche · Bar · leurre · bord, kayak » : même ordre que le nom des profils.
  const fishing = fish ? ['Pêche', fish.name, ...lower(chosenEsches), ...lower(chosenPostes)].join(' · ') : `Pêche · ${keptNames || '—'}`;
  const summary = draft.category === 'peche' ? fishing : `${CATEGORY_LABELS[draft.category]} · ${names(draft.profiles).join(', ')}`;
  // Modification d'une composition gardée telle quelle : enregistrable sans espèce cochée.
  const profilesReady = draft.category === 'peche' ? draft.fish !== null || draft.kept.length > 0 : draft.profiles.length > 0;
  const defaultName = draft.category === 'peche' && fish ? fishing : CATEGORY_LABELS[draft.category];

  function cancel() {
    ephemeral.draft = null;
    navigate(draft?.tableId ? '/compte' : '/');
  }

  function goToPlaces() {
    update({ step: 3, name: draft!.nameTouched ? draft!.name : defaultName });
  }

  async function submit() {
    const current = draft!;
    const profiles =
      current.category === 'peche'
        ? current.fish === null
          ? []
          : combinations(chosenPostes.map((poste) => poste.id), chosenEsches.map((esche) => esche.id)).map(({ posteId, escheId }) => {
              // Valeur affichée envoyée seulement si elle diffère du défaut d'un poste ou d'une esche du profil qui la porte.
              const shown = new Map(panelLimits(chosenParts, current.limits).map((limit) => [limit.name, limit.value]));
              const parts = chosenParts.filter((part) => part.id === posteId || part.id === escheId);
              const own = Object.fromEntries(parts.flatMap((part) => part.params.flatMap((param) => (shown.get(param.name) !== param.default ? [[param.name, shown.get(param.name)!]] : []))));
              return { templateId: current.fish!, posteId, escheId, params: Object.keys(own).length > 0 ? own : null };
            })
        : current.profiles.map((templateId) => ({ templateId, posteId: null, escheId: null, params: null }));
    // Un profil conservé que l'édition redéfinit (même espèce, poste et esche) n'est pas envoyé deux fois.
    const edited = new Set(profiles.map(profileKey));
    const kept = current.kept
      .filter((profile) => !edited.has(profileKey(profile)))
      .map((profile) => ({ templateId: profile.templateId, posteId: profile.posteId, escheId: profile.escheId, params: profile.params }));
    const body = { name: current.name.trim() || defaultName, profiles: [...profiles, ...kept], locationIds: current.places };
    setBusy(true);
    setError(null);
    try {
      if (current.tableId) await api('PATCH', `/tables/${current.tableId}`, body);
      else await api('POST', '/tables', body);
      ephemeral.draft = null;
      navigate('/', { state: { created: body.name } });
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const title = draft.step === 3 ? 'Où regarder ?' : draft.tableId ? 'Modifier le tableau' : 'Nouveau tableau';

  return (
    <div className="screen">
      <header className="screen-head compact wizard-head">
        <div className="head-row">
          <button type="button" className="link-btn" onClick={cancel}>
            Annuler
          </button>
          <span className="meta">ÉTAPE {draft.step} / 3</span>
        </div>
        <h1 className="h1-wizard">{title}</h1>
        <div className="progress" aria-hidden="true">
          {[1, 2, 3].map((step) => (
            <span key={step} className={step <= draft.step ? 'done' : undefined} />
          ))}
        </div>
        {draft.step === 2 && (
          <div className="chips" role="group" aria-label="Catégorie">
            {CATEGORIES.map((category) => (
              <button key={category} type="button" className="chip" aria-pressed={category === draft.category} onClick={() => update({ category, profiles: [], fish: null, postes: [], esches: [], limits: {} })}>
                {CATEGORY_LABELS[category]}
              </button>
            ))}
          </div>
        )}
        {draft.step === 3 && <p className="wizard-summary">{summary}</p>}
      </header>

      <main className="screen-body wizard-body">
        {catalog.error && <FormError error={catalog.error} />}
        {!catalog.data && !catalog.error && <Loading />}

        {catalog.data && draft.step === 1 && (
          <>
            <span className="section-label">Que voulez-vous suivre ?</span>
            <div className="list">
              {CATEGORIES.map((category) => {
                const count = templates.filter((template) => template.category === category && template.role !== 'poste' && template.role !== 'esche').length;
                return (
                  <button key={category} type="button" className="row tall" onClick={() => update({ category, step: 2, profiles: [], fish: null, postes: [], esches: [], limits: {} })}>
                    <span className="row-main">
                      <span>{CATEGORY_LABELS[category]}</span>
                      <span>
                        {count} {category === 'peche' ? (count > 1 ? 'espèces' : 'espèce') : count > 1 ? 'profils' : 'profil'}
                      </span>
                    </span>
                    <span className="chevron" aria-hidden="true">
                      ›
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {catalog.data && draft.step === 2 && draft.category !== 'peche' && (
          <ProfilesStep
            category={draft.category}
            profiles={species}
            selected={draft.profiles}
            offSeason={offSeason}
            onChange={(profiles) => update({ profiles })}
          />
        )}

        {catalog.data && draft.step === 2 && draft.category === 'peche' && (
          <FishingStep
            species={species}
            postes={postes}
            esches={esches}
            draft={draft}
            kept={keptNames}
            offSeason={offSeason}
            onChange={update}
          />
        )}

        {draft.step === 3 && (
          <>
            <label className="field wizard-name">
              <span>Nom du tableau</span>
              <input className="input input-48" value={draft.name} maxLength={100} onChange={(event) => update({ name: event.target.value, nameTouched: true })} />
            </label>
            <span className="section-label wizard-places-label">
              Mes lieux · {draft.places.length} {draft.places.length > 1 ? 'sélectionnés' : 'sélectionné'}
            </span>
            {!locations.data && <Loading />}
            {locations.data && (
              <div className="list">
                {locations.data.locations.map((location) => (
                  <button key={location.id} type="button" className="row tall" role="checkbox" aria-checked={draft.places.includes(location.id)} onClick={() => update({ places: toggle(draft.places, location.id) })}>
                    <Box on={draft.places.includes(location.id)} />
                    <span className="row-main">
                      <span>{location.name}</span>
                      <span>{placeKind(location)}</span>
                    </span>
                    {location.mine && !location.isPublic && <span className="tag outline">PRIVÉ</span>}
                  </button>
                ))}
              </div>
            )}
            <Link to="/lieux" state={{ returnTo: '/creer' + (draft.tableId ? `?tableau=${draft.tableId}` : '') }} className="btn-dashed">
              + Ajouter un lieu sur la carte
            </Link>
            {draft.kept.length > 0 && <p className="help">Profils conservés en plus : {keptNames}.</p>}
            <FormError error={error} />
          </>
        )}
      </main>

      {draft.step > 1 && (
        <footer className="screen-foot">
          <button type="button" className="btn btn-secondary" onClick={() => update({ step: (draft.step - 1) as 1 | 2 })}>
            Retour
          </button>
          {draft.step === 2 ? (
            <button type="button" className="btn btn-primary btn-grow" disabled={!profilesReady} onClick={goToPlaces}>
              Choisir les lieux
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-grow" disabled={draft.places.length === 0 || busy} onClick={() => void submit()}>
              {draft.tableId ? 'Enregistrer le tableau' : 'Créer le tableau'}
            </button>
          )}
        </footer>
      )}
      {draft.step === 1 && <TabBar />}
    </div>
  );
}

/** Sous-titre d'un lieu : côtier (orientation de la mer), public ou partagé, sinon privé. */
export function placeKind(location: LocationDto): string {
  if (location.seaOrientation) return `Côtier · mer ${SEA_TEXT[location.seaOrientation]}`;
  if (!location.mine) return 'Lieu public';
  return location.isPublic ? 'Lieu partagé' : 'Lieu privé';
}

export const SEA_TEXT: Record<NonNullable<LocationDto['seaOrientation']>, string> = {
  N: 'au nord',
  NE: 'au nord-est',
  E: 'à l’est',
  SE: 'au sud-est',
  S: 'au sud',
  SO: 'au sud-ouest',
  O: 'à l’ouest',
  NO: 'au nord-ouest',
};

function ProfilesStep({
  category,
  profiles,
  selected,
  offSeason,
  onChange,
}: {
  category: Category;
  profiles: CatalogTemplateDto[];
  selected: number[];
  offSeason: (template: CatalogTemplateDto) => boolean;
  onChange: (selected: number[]) => void;
}) {
  const open = profiles.filter((profile) => !offSeason(profile));
  const all = open.length > 0 && open.every((profile) => selected.includes(profile.id));
  const chosen = open.filter((profile) => selected.includes(profile.id)).length;
  return (
    <>
      <div className="head-row profiles-head">
        <span className="section-label">
          Profils · {chosen} / {open.length} en saison
        </span>
        <button
          type="button"
          className={`pack${all ? ' on' : ''}`}
          aria-pressed={all}
          onClick={() => onChange(all ? selected.filter((id) => !open.some((profile) => profile.id === id)) : [...new Set([...selected, ...open.map((profile) => profile.id)])])}
        >
          {all ? `✓ ${PACK_LABELS[category]}` : PACK_LABELS[category]}
        </button>
      </div>
      <div className="list">
        {profiles.map((profile) => {
          const off = offSeason(profile);
          // Hors saison : pas d'ajout, mais un profil déjà dans le tableau (modification) se retire.
          const on = selected.includes(profile.id);
          const locked = off && !on;
          return (
            <button
              key={profile.id}
              type="button"
              className="row"
              role="checkbox"
              aria-checked={on}
              aria-disabled={locked}
              onClick={() => !locked && onChange(toggle(selected, profile.id))}
            >
              <Box on={on} />
              <span className="row-main">
                <span>{profile.name}</span>
                <span>{seasonLabel(profile.season)}</span>
              </span>
              {off && <StatusPill status="hors_saison" />}
            </button>
          );
        })}
      </div>
      <p className="help">Les profils hors saison restent sélectionnables plus tard : ils s’activeront à l’ouverture de la saison.</p>
    </>
  );
}

function FishingStep({
  species,
  postes,
  esches,
  draft,
  kept,
  offSeason,
  onChange,
}: {
  species: CatalogTemplateDto[];
  postes: CatalogTemplateDto[];
  esches: CatalogTemplateDto[];
  draft: Draft;
  /** Profils gardés tels quels (modification), déjà nommés. */
  kept: string;
  offSeason: (template: CatalogTemplateDto) => boolean;
  onChange: (patch: Partial<Draft>) => void;
}) {
  const chosenFish = species.find((template) => template.id === draft.fish);
  const chosen = [...postes, ...esches].filter((part) => draft.postes.includes(part.id) || draft.esches.includes(part.id));
  const tunable = chosen.filter((part) => part.params.length > 0);
  // Une limite vaut pour tous les postes et esches cochés qui l'acceptent (voir panelLimits).
  const params = panelLimits(tunable, draft.limits);
  const navigating = postes.some((poste) => poste.navigation && draft.postes.includes(poste.id));
  const nameOf = (parts: CatalogTemplateDto[], id: number | null) => parts.find((part) => part.id === id)?.name ?? null;
  const profiles = chosenFish
    ? combinations(
        postes.filter((poste) => draft.postes.includes(poste.id)).map((poste) => poste.id),
        esches.filter((esche) => draft.esches.includes(esche.id)).map((esche) => esche.id),
      ).map(({ posteId, escheId }) => profileName(chosenFish.name, nameOf(esches, escheId), nameOf(postes, posteId)))
    : [];
  const choice = (parts: CatalogTemplateDto[], selected: number[], key: 'postes' | 'esches') =>
    parts.map((part) => {
      const on = selected.includes(part.id);
      return (
        <button key={part.id} type="button" className={`technique${on ? ' on' : ''}`} role="checkbox" aria-checked={on} onClick={() => onChange({ [key]: toggle(selected, part.id) })}>
          <Box on={on} small />
          <span>{part.name}</span>
        </button>
      );
    });
  return (
    <div className="fishing">
      <section className="fishing-part">
        <span className="section-label">1 · Espèce</span>
        <div className="species">
          {species.map((fish) => {
            const off = offSeason(fish);
            return (
              <button key={fish.id} type="button" className="species-btn" aria-pressed={draft.fish === fish.id} disabled={off} onClick={() => onChange({ fish: fish.id })}>
                {off ? `${fish.name} · hors saison` : fish.name}
              </button>
            );
          })}
        </div>
        {draft.tableId && kept && <p className="help">Gardés tels quels dans le tableau : {kept}.</p>}
      </section>
      <section className="fishing-part">
        <span className="section-label">2 · Poste</span>
        <div className="techniques">{choice(postes, draft.postes, 'postes')}</div>
        <p className="help">Facultatif : sans poste, l’espèce est suivie seule.</p>
      </section>
      {esches.length > 0 && (
        <section className="fishing-part">
          <span className="section-label">3 · Leurre ou appât</span>
          <div className="techniques">{choice(esches, draft.esches, 'esches')}</div>
          <p className="help">Facultatif : le leurre retient les créneaux qui touchent l’aube ou le crépuscule.</p>
        </section>
      )}
      {profiles.length > 0 && (
        <p className="help profiles-count" role="status" aria-atomic="true">
          <strong>
            {profiles.length} {profiles.length > 1 ? 'profils' : 'profil'}
          </strong>{' '}
          : {profiles.join(', ')}
        </p>
      )}
      {params.length > 0 && (
        <section className="limits">
          <div className="head-row baseline">
            <h2 className="panel-title">Mes limites</h2>
            <span className="limits-for">{tunable.map((part) => part.name.toLocaleLowerCase('fr-FR')).join(' + ')}</span>
          </div>
          {params.map((param) => {
            const config = sliderBounds(tunable, param);
            const { value } = param;
            return (
              <label key={param.name} className="limit">
                <span className="limit-row">
                  <span>{config.label}</span>
                  <span className="mono">
                    {decimal(value)} {config.unit}
                  </span>
                </span>
                <input
                  type="range"
                  min={config.min}
                  max={config.max}
                  step={config.step}
                  value={value}
                  onChange={(event) => onChange({ limits: { ...draft.limits, [param.name]: Number(event.target.value) } })}
                />
              </label>
            );
          })}
          {navigating && <MarineBanner />}
        </section>
      )}
    </div>
  );
}
